// Goods in: the CofA register.
//
// Every ingredient delivery due or received in a window, the certificate found
// for it, and what somebody has to do next. It replaces the outstanding tab of
// the raw-material release spreadsheet and the Monday "due this week" email.
//
// THE ONE QA STEP IS THE DECISION (Amit, 24 Oct 2026). The count of lines
// awaiting one heads the page, and the table opens with them first, oldest
// first (registerOrder; lines D365 could not be read above them, rule 15), then
// the API's order: goods on site without an approved certificate first. A header click sorts
// only because the reader asked, and the third click returns to it. Those rows
// carry a red bar at their left edge however the table is sorted — a bar, not a
// row fill: with every received line red, the fill read as one red wall
// (2026-10-01). "Needs attention only" hides lines with nothing to do — never a
// line that has something.
//
// It is a SNAPSHOT of D365, and says so: "Refresh from D365" is the only way it
// changes, and a window that was never refreshed says that rather than "nothing
// due" (rule 15). Approval is per batch: a line reads Approved only when every
// batch D365 received was in front of an approval.
import { useCallback, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { DataTable, type RowAccent } from '@/components/DataTable';
import { VerdictBadge } from '../../components/VerdictBadge';
import { HoldsPanel } from './BatchHolds';
import { GoodsInIntake } from './GoodsInIntake';
import { GoodsInNotices } from './GoodsInNotices';
import { GoodsInSuppliers } from './GoodsInSuppliers';
import { api } from '../../lib/api';
import { fieldClass, labelClass, officeFocus, serverMessage, useResource } from '../../shells/OfficeShell';
import {
  AUTOMATIC_APPROVAL,
  AUTOMATIC_APPROVAL_WHY,
  FLAG_LABEL,
  FLAG_TONE,
  iirRegisterWord,
  STATE_TONE,
  TONE_CLASS,
  TONE_TEXT,
  batchSummary,
  chipClass,
  dayLabel,
  localDay,
  receivedWithUnit,
  registerOrder,
  registerStateText,
  stamp,
  type CofaState,
  type RegisterFlag,
  type RegisterPayload,
  type RegisterRow,
} from './goodsInModel';

/* ── The register's columns ─────────────────────────────── */

const col = createColumnHelper<RegisterRow>();

/** Most urgent first — the API's own flag order — for sorting on Needs. */
const FLAG_RANK: RegisterFlag[] = [
  'BATCH_READ_FAILED',
  'RECEIVED_NOT_APPROVED',
  'BATCH_OUTSIDE_APPROVAL',
  'BATCH_NOT_EXACT',
  'NO_BATCH_IN_D365',
  'REQUEST_DUE',
  'REMINDER_DUE',
  'DELIVERY_OVERDUE',
];
const flagRank = (r: RegisterRow) =>
  r.flags.length === 0 ? FLAG_RANK.length : Math.min(...r.flags.map((f) => FLAG_RANK.indexOf(f)));
/** The next thing to do, most urgent first, for sorting on Certificate. */
const STATE_RANK: CofaState[] = [
  'BATCHES_UNKNOWN',
  'AWAITING_DECISION',
  'REJECTED',
  'NEW_COFA_REQUESTED',
  'NOT_FOUND',
  'APPROVED',
  'ON_PAPER',
];

const columns = [
  // Order (UI/UX audit, 31 Oct 2026, UX-15): PO first and pinned (stickyFirst) as the row's
  // name, then Due, then Certificate and Needs - what to do - so at iPad width (~696px) the
  // decision columns are on screen; Item, Supplier and Received follow.
  col.accessor((r) => `${r.po}${r.line !== 1 ? ` / ${r.line}` : ''}`, {
    id: 'po',
    header: 'PO',
    cell: (c) => (
      <Link
        to={`/office/goods-in/${c.row.original.receiptId}`}
        className={`underline decoration-(--border-strong) underline-offset-[3px] hover:decoration-current ${officeFocus}`}
      >
        {c.getValue()}
      </Link>
    ),
    meta: { rowHeader: true, mono: true, nowrap: true, width: '8.5rem' },
  }),
  col.accessor((r) => r.expectedDate ?? '', {
    id: 'due',
    header: 'Due',
    cell: (c) => dayLabel(c.row.original.expectedDate),
    meta: { nowrap: true, width: '6.5rem' },
  }),
  col.accessor((r) => registerStateText(r), {
    id: 'certificate',
    header: 'Certificate',
    sortingFn: (a, b) => STATE_RANK.indexOf(a.original.state) - STATE_RANK.indexOf(b.original.state),
    cell: (c) => {
      const r = c.row.original;
      return (
        <>
          {/* (UI/UX audit, 31 Oct 2026, UX-23) The state's own words, fuller when every batch is
              already approved; it wraps rather than run into the next column. */}
          <span className={`${chipClass} ${TONE_CLASS[STATE_TONE[r.state]]}`}>{registerStateText(r)}</span>
          {r.certificate?.decision?.automatic && r.certificate.decision.decision === 'APPROVED' ? (
            <span className={`${chipClass} mt-[2px] flex w-fit whitespace-nowrap ${TONE_CLASS.quiet}`} title={`${AUTOMATIC_APPROVAL} ${AUTOMATIC_APPROVAL_WHY}`}>
              {AUTOMATIC_APPROVAL}
            </span>
          ) : null}
          {/* The verdict is a recommendation about the results, not a second task (UX-23): said as
              "Results: needs review", quieter than the state pill above it. */}
          {r.certificate?.verdict ? <VerdictBadge verdict={r.certificate.verdict} label="Results" className="mt-[2px] block" /> : null}
          {r.certificate?.readMode === 'MANUAL_READ' ? (
            <span className="mt-[2px] block text-(length:--fs-xs) text-(--muted)">read by hand</span>
          ) : null}
        </>
      );
    },
    meta: { width: '10rem' },
  }),
  col.accessor((r) => r.flags.map((f) => FLAG_LABEL[f]).join(' '), {
    id: 'needs',
    header: 'Needs',
    sortingFn: (a, b) => flagRank(a.original) - flagRank(b.original),
    cell: (c) => {
      const ch = c.row.original.chases;
      return (
        <>
          <ul className="m-0 flex list-none flex-wrap gap-[4px] p-0">
            {c.row.original.flags.map((f) => (
              <li key={f} className={`${chipClass} whitespace-nowrap ${TONE_CLASS[FLAG_TONE[f]]}`}>
                {FLAG_LABEL[f]}
              </li>
            ))}
          </ul>
          {/* Below xl the Chased column is folded in here (UI/UX audit, 31 Oct 2026, UX-15): the
              chase is what was done about the need, so it reads under it rather than off-screen. */}
          {ch.last ? (
            <span className="mt-[2px] block text-(length:--fs-xs) text-(--muted) xl:hidden">
              Chased {ch.count} · {stamp(ch.last.loggedAt)}
            </span>
          ) : null}
        </>
      );
    },
    meta: { width: '11rem' },
  }),
  col.accessor((r) => `${r.itemName ?? r.item} ${r.item}`, {
    id: 'item',
    header: 'Item',
    sortingFn: (a, b) => (a.original.itemName ?? a.original.item).localeCompare(b.original.itemName ?? b.original.item),
    cell: (c) => {
      const r = c.row.original;
      return (
        <>
          <span className="line-clamp-2" title={r.itemName ?? r.item}>
            {r.itemName ?? r.item}
          </span>
          <span className="block font-mono text-(length:--fs-xs) text-(--muted)">{r.item}</span>
        </>
      );
    },
    meta: { width: '15rem' },
  }),
  col.accessor((r) => r.vendor.name ?? r.vendor.account ?? '', {
    id: 'supplier',
    header: 'Supplier',
    cell: (c) => (
      <>
        <span className="line-clamp-2" title={c.getValue()}>
          {c.getValue() || '—'}
        </span>
        {/* D365's hold on the vendor (Suppliers, migration 039): flagged on its lines, never hidden. */}
        {c.row.original.vendor.onHold && c.row.original.vendor.onHold !== 'No' ? (
          <span className={`${chipClass} ${TONE_CLASS.warn}`}>On hold in D365</span>
        ) : null}
      </>
    ),
    meta: { width: '13rem' },
  }),
  col.accessor(
    (r) => {
      const s = batchSummary(r);
      return `${s.text} ${s.approval?.text ?? ''} ${s.ids.join(' ')}`;
    },
    {
      id: 'received',
      header: 'Received',
      sortingFn: (a, b) =>
        Number(a.original.received ?? -1) - Number(b.original.received ?? -1) ||
        a.original.batches.length - b.original.batches.length,
      cell: (c) => {
        const s = batchSummary(c.row.original);
        // "2100 kg" only when the stock unit is known: a bare number would not say what it counts.
        const r = c.row.original;
        const qty = !r.batchReadError && (r.receivedQty ?? 0) > 0 ? receivedWithUnit(r) : null;
        return (
          <>
            {/* Wraps (UI/UX audit, 31 Oct 2026, UX-15): kept on one line it was the widest cell
                in the register and pushed Certificate and Needs past an iPad's edge. */}
            <span>
              {s.text}
              {qty ? ` · ${qty}` : null}
              {s.approval ? (
                <span className={`font-semibold ${TONE_TEXT[s.approval.tone]}`}> · {s.approval.text}</span>
              ) : null}
            </span>
            {s.ids.length > 0 ? (
              <span className="block truncate font-mono text-(length:--fs-xs) text-(--muted)" title={s.ids.join(', ')}>
                {s.ids.slice(0, 2).join(', ')}
                {s.ids.length > 2 ? ` +${s.ids.length - 2} more` : ''}
              </span>
            ) : null}
          </>
        );
      },
      meta: { width: '14rem' },
    },
  ),
  // The dock's inspection: beside the certificate, never a condition of it. A blank cell is
  // an API that does not know (rule 15), never "not inspected".
  col.accessor((r) => (r.iir ? iirRegisterWord(r.iir).text : ''), {
    id: 'iir',
    header: 'Inspection',
    cell: (c) => {
      const iir = c.row.original.iir;
      if (!iir) return '—';
      return (
        <>
          <span className={`${chipClass} ${iir.goodsPending ? '' : 'whitespace-nowrap'} ${TONE_CLASS[iirRegisterWord(iir).tone]}`}>{iirRegisterWord(iir).text}</span>
          {iir.inspectedAt ? <span className="mt-[2px] block text-(length:--fs-xs) text-(--muted)">{stamp(iir.inspectedAt)}</span> : null}
        </>
      );
    },
    meta: { width: '8rem' },
  }),
  col.accessor((r) => r.chases.last?.loggedAt ?? '', {
    id: 'chased',
    header: 'Chased',
    cell: (c) => {
      const ch = c.row.original.chases;
      return ch.last ? `${ch.count} · ${stamp(ch.last.loggedAt)}` : '—';
    },
    // Folded into Needs below xl (UX-15); its own column from xl up.
    meta: { nowrap: true, width: '9rem', cellClass: 'hidden xl:table-cell' },
  }),
];

/** Red for goods on site that no approval covers, or a line D365 could not
 *  read; amber for QA's own queue. Nothing else gets a bar. */
function rowAccent(r: RegisterRow): RowAccent {
  if (r.flags.includes('RECEIVED_NOT_APPROVED') || r.flags.includes('BATCH_READ_FAILED')) return 'bad';
  if (r.state === 'AWAITING_DECISION') return 'warn';
  return null;
}

interface SyncResult {
  lines: number;
  newLines: number;
  receivedLines: number;
  batches: number;
  syncedAt: string;
  warnings: string[];
}

/* ── The page's tabs (UI/UX audit, 27 Oct 2026, item 21) ──────
 * The register, the QC Holds, the Intake and the Teams chat were four scroll areas stacked
 * down one page. They are tabs now. Every panel stays MOUNTED and is hidden when it is not
 * the one selected, so each still loads exactly as it did (and the rule-15 "not known"
 * sentences are still there when its tab is opened). The selected tab is in the URL as
 * `?tab=`; a `?hold=<id>` link (the cards' link) or `#holds` lands on QC Holds. */
const TABS = [
  { id: 'register', label: 'Register' },
  { id: 'holds', label: 'QC Holds' },
  { id: 'intake', label: 'Intake' },
  // (UI/UX audit, 31 Oct 2026, UX-48) The panel only checks for cards the chat missed, so it is
  // named for the cards. The id stays `chat`: `?tab=chat` links already made keep working.
  { id: 'chat', label: 'Teams cards' },
  { id: 'suppliers', label: 'Suppliers' },
] as const;
type TabId = (typeof TABS)[number]['id'];
/** What each tab's count is of, for its accessible name (and its tooltip). (UI/UX audit, 31 Oct
 *  2026, UX-03) Intake counts QA@ ATTACHMENTS, not decisions: beside the nav badge's "2 need a
 *  decision", a bare "need a person" read as the same thing disagreeing. They are never summed. */
const TAB_COUNT_WORDS: Record<TabId, string> = { register: 'awaiting decision', holds: 'open holds', intake: 'QA@ attachments need a person', chat: 'cards missed', suppliers: 'need settings' };
const isTab = (s: string | null): s is TabId => TABS.some((t) => t.id === s);

/** Which tab the address names: `tab` if it is one, else a hold link, else the register. */
export function tabFromAddress(params: URLSearchParams, hash: string): TabId {
  const tab = params.get('tab');
  if (isTab(tab)) return tab;
  if (params.get('hold') || hash === '#holds') return 'holds';
  return 'register';
}

export function GoodsIn() {
  // The other tabs' counts, each reported by its panel from the read it already makes.
  const [tabCounts, setTabCounts] = useState<Partial<Record<TabId, number | null>>>({});
  const countHolds = useCallback((n: number | null) => setTabCounts((c) => (c.holds === n ? c : { ...c, holds: n })), []);
  const countIntake = useCallback((n: number | null) => setTabCounts((c) => (c.intake === n ? c : { ...c, intake: n })), []);
  const countChat = useCallback((n: number | null) => setTabCounts((c) => (c.chat === n ? c : { ...c, chat: n })), []);
  const countSuppliers = useCallback((n: number | null) => setTabCounts((c) => (c.suppliers === n ? c : { ...c, suppliers: n })), []);
  const [from, setFrom] = useState(localDay(-14));
  const [to, setTo] = useState(localDay(14));
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const [params, setParams] = useSearchParams();
  const { hash } = useLocation();
  const tab = tabFromAddress(params, hash);
  const tabRefs = useRef<Partial<Record<TabId, HTMLButtonElement | null>>>({});
  function selectTab(id: TabId, focus = false) {
    const next = new URLSearchParams(params);
    next.set('tab', id);
    setParams(next, { replace: true });
    if (focus) tabRefs.current[id]?.focus();
  }
  // Arrow keys move between tabs and select as they go; Home and End reach the ends.
  function onTabKey(e: KeyboardEvent<HTMLButtonElement>) {
    const i = TABS.findIndex((t) => t.id === tab);
    const to =
      e.key === 'ArrowRight' ? (i + 1) % TABS.length
      : e.key === 'ArrowLeft' ? (i - 1 + TABS.length) % TABS.length
      : e.key === 'Home' ? 0
      : e.key === 'End' ? TABS.length - 1
      : -1;
    if (to < 0) return;
    e.preventDefault();
    selectTab(TABS[to].id, true);
  }

  const query = new URLSearchParams({ from, to });
  if (attentionOnly) query.set('attentionOnly', 'true');
  const register = useResource<RegisterPayload>(`/receipts?${query.toString()}`);
  const rows = registerOrder(register.data?.rows ?? []);

  async function refresh() {
    setSyncing(true);
    setSyncError(null);
    setSyncResult(null);
    try {
      setSyncResult(await api.post<SyncResult>('/receipt-sync', { from, to }));
      register.reload();
    } catch (err: unknown) {
      setSyncError(serverMessage(err));
    } finally {
      setSyncing(false);
    }
  }

  const byFlag = register.data?.byFlag ?? {};
  const byState = register.data?.byState ?? {};
  const summary = [
    byFlag.BATCH_READ_FAILED ? `${byFlag.BATCH_READ_FAILED} could not be read from D365` : null,
    byFlag.RECEIVED_NOT_APPROVED ? `${byFlag.RECEIVED_NOT_APPROVED} received and not approved` : null,
    byFlag.REQUEST_DUE ? `${byFlag.REQUEST_DUE} request${byFlag.REQUEST_DUE === 1 ? '' : 's'} due` : null,
    byFlag.REMINDER_DUE ? `${byFlag.REMINDER_DUE} reminder${byFlag.REMINDER_DUE === 1 ? '' : 's'} due` : null,
  ].filter(Boolean);

  // The register itself, a value rather than an inner component: a component declared inside
  // render is a new type every render, and its table would forget the sort and the search.
  const registerTable = (
    <DataTable
      data={rows}
      columns={columns}
      caption="Ingredient deliveries and their certificates, awaiting decision first, oldest first"
      getRowId={(r) => String(r.receiptId)}
      rowAccent={rowAccent}
      rowHref={(r) => `/office/goods-in/${r.receiptId}`}
      stickyFirst
      loading={register.loading}
      error={register.error}
      onRetry={register.reload}
      search="Search PO, item, supplier or batch"
      toolbar={
        <label className="flex min-h-[44px] items-center gap-(--s2) text-(length:--fs-sm)">
          <input
            type="checkbox"
            className={officeFocus}
            checked={attentionOnly}
            onChange={(e) => setAttentionOnly(e.target.checked)}
          />
          Needs attention only
        </label>
      }
      empty={
        register.data
          ? register.data.windowSyncedAt
            ? attentionOnly
              ? 'Nothing in this window needs attention.'
              : 'No ingredient deliveries in this window.'
            : 'This window has not been refreshed from D365, so this is not a list of what is due.'
          : null
      }
    />
  );

  // The register's own controls (UI/UX audit, 31 Oct 2026, UX-24): the date window, Refresh and
  // its result, and the register's counts ("21 received and not approved") used to sit above the
  // tabs, on every tab, and on a phone pushed the table to y=785. They are about the register
  // alone, so they live in its panel. The h1, the awaiting-decision count and the page-wide
  // warnings (D365's, open inspection failures) stay above the tabs.
  const registerControls = (
    <>
      <div className="flex flex-wrap items-end gap-(--s3)">
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>From</span>
          <input type="date" className={`${fieldClass} ${officeFocus}`} value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>To</span>
          <input type="date" className={`${fieldClass} ${officeFocus}`} value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        {/* Outline: the page's primary action is the decision on the detail page, not the refresh. */}
        <Button variant="outline" type="button" onClick={() => void refresh()} disabled={syncing}>
          {syncing ? 'Reading D365…' : 'Refresh from D365'}
        </Button>
        <p className="m-0 text-(length:--fs-xs) text-(--muted)" aria-live="polite">
          {register.data
            ? register.data.windowSyncedAt
              ? `This window refreshed ${stamp(register.data.windowSyncedAt)}`
              : 'This window has not been refreshed from D365'
            : ''}
        </p>
      </div>

      {syncError ? (
        <p role="alert" className={`m-0 rounded-(--radius) border p-(--s3) text-(length:--fs-md) font-semibold ${TONE_CLASS.warn}`}>
          {syncError}
        </p>
      ) : null}
      {syncResult ? (
        <p role="status" className="m-0 text-(length:--fs-sm) text-(--muted)">
          Refreshed {syncResult.lines} line{syncResult.lines === 1 ? '' : 's'} ({syncResult.newLines} new,{' '}
          {syncResult.receivedLines} received, {syncResult.batches} batch{syncResult.batches === 1 ? '' : 'es'}) at{' '}
          {stamp(syncResult.syncedAt)}.
          {syncResult.warnings.map((w) => ` ${w}`)}
        </p>
      ) : null}

      {summary.length > 0 ? (
        <p className="m-0 text-(length:--fs-md) font-semibold">{summary.join(' · ')}</p>
      ) : null}
    </>
  );

  return (
    <div className="flex flex-col gap-(--s5)">
      <header>
        <h1 className="m-0 font-display text-[34px] font-semibold leading-none tracking-[-0.01em]">Goods in</h1>
        <p className="m-0 mt-(--s1) max-w-[80ch] text-(length:--fs-sm) text-(--muted)">
          Ingredient deliveries and their certificates. Certificates are recorded automatically; each batch is decided here.
        </p>
      </header>

      {register.data ? (
        // The one QA step (Amit, 24 Oct 2026): how many lines wait for it, oldest first in the table.
        <p role="status" aria-label="Awaiting decision" className="m-0 font-display text-[22px] font-semibold leading-tight">
          Awaiting decision: {byState.AWAITING_DECISION ?? 0}
          <span className="ml-(--s2) font-sans text-(length:--fs-sm) font-normal text-(--muted)">
            in this window, oldest first below
          </span>
        </p>
      ) : null}

      {(register.data?.warnings ?? []).map((w) => (
        <p key={w} role="alert" className={`m-0 rounded-(--radius) border p-(--s3) text-(length:--fs-sm) font-semibold ${TONE_CLASS.warn}`}>
          {w}
        </p>
      ))}


      {/* Open inspection failures across goods in (migration 020). Absent from the payload is
          "not known", never none (rule 15). */}
      {register.data && !register.data.inspectionFailures ? (
        <p role="status" className="m-0 text-(length:--fs-sm) text-(--muted)">
          Inspection failures: not known - the server did not say.{' '}
          <Link to="/office/inspection-failures" className={`underline ${officeFocus}`}>
            Inspection failures
          </Link>
        </p>
      ) : null}
      {register.data?.inspectionFailures && register.data.inspectionFailures.open > 0 ? (
        <p role="status" className={`m-0 w-fit rounded-(--radius) border p-(--s3) text-(length:--fs-sm) font-semibold ${TONE_CLASS.bad}`}>
          {register.data.inspectionFailures.open} open inspection failure{register.data.inspectionFailures.open === 1 ? '' : 's'} at the
          dock.{' '}
          <Link to="/office/inspection-failures" className={`underline ${officeFocus}`}>
            Inspection failures
          </Link>
        </p>
      ) : null}

      <div role="tablist" aria-label="Goods in" className="flex flex-wrap gap-(--s1) border-b border-(--border)">
        {TABS.map((t) => {
          const selected = t.id === tab;
          // The register's count is the page's own (awaiting decision, above); each other panel
          // reports its own count from the read it already makes (onCount), so nothing is read twice.
          const count = t.id === 'register' ? (register.data ? (byState.AWAITING_DECISION ?? 0) : null) : (tabCounts[t.id] ?? null);
          return (
            <button
              key={t.id}
              ref={(el) => {
                tabRefs.current[t.id] = el;
              }}
              type="button"
              role="tab"
              id={`goodsin-tab-${t.id}`}
              aria-selected={selected}
              aria-controls={`goodsin-panel-${t.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => selectTab(t.id)}
              onKeyDown={onTabKey}
              className={`-mb-px inline-flex min-h-[44px] items-center gap-(--s2) border-b-2 px-(--s3) text-(length:--fs-sm) font-semibold ${officeFocus} ${
                selected ? 'border-(--accent-line) text-(--fg)' : 'border-transparent text-(--muted) hover:text-(--fg)'
              }`}
            >
              {t.label}
              {count !== null && count > 0 ? (
                <span className={`${chipClass} ${TONE_CLASS[selected ? 'warn' : 'quiet']}`} aria-label={`${count} ${TAB_COUNT_WORDS[t.id]}`} title={`${count} ${TAB_COUNT_WORDS[t.id]}`}>
                  {count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {TABS.map((t) => (
        <div
          key={t.id}
          role="tabpanel"
          id={`goodsin-panel-${t.id}`}
          aria-labelledby={`goodsin-tab-${t.id}`}
          tabIndex={0}
          hidden={t.id !== tab}
          className="flex flex-col gap-(--s5)"
        >
          {t.id === 'register' ? (
            <>
              {registerControls}
              {registerTable}
            </>
          ) : t.id === 'holds' ? (
            <HoldsPanel onCount={countHolds} />
          ) : t.id === 'intake' ? (
            <GoodsInIntake onCount={countIntake} />
          ) : t.id === 'chat' ? (
            <GoodsInNotices onCount={countChat} />
          ) : (
            <GoodsInSuppliers onCount={countSuppliers} />
          )}
          {/* A panel whose component rendered nothing (notices switched off) says so rather than
              standing empty: `only:` is :only-child, true exactly then. (UI/UX audit, 31 Oct 2026,
              UX-10) The holds branch is gone: HoldsPanel now says loading, unread or off itself -
              this sentence showed while its switch was merely loading. */}
          <p className="m-0 hidden text-(length:--fs-sm) text-(--muted) only:block">Nothing to show here.</p>
        </div>
      ))}
    </div>
  );
}

export default GoodsIn;
